import { useCallback, useMemo, useState } from 'react';
import { Toolbar, ToolbarContent, ToolbarItem, Button, Modal, ModalVariant, Alert, TextContent, Text, MenuToggle, Dropdown, DropdownItem } from '@patternfly/react-core';
import { useSelector } from 'react-redux';
import { SaveIcon, UndoIcon, PlusIcon, TrashIcon, PencilAltIcon, EllipsisVIcon, HistoryIcon } from '@patternfly/react-icons'
import { Trans, useTranslation } from 'react-i18next';

import { CustomizationBase, CustomizationObjectType, CustomizationOutput, DEFAULT_MODEL_CUSTOMIZATION_NAME, Model, ModelType } from '../../store/global-types/customization-types';
import { ConcurrentSaveError } from '../../util/errors';
import { generateNewId } from '../../store/global-types/customization-helpers';
import CustomizationNameField, { defaultDuplicatedModelNameValidation, validateDuplicatedModelName } from './CustomizationNameField';
import RenameCustomizationModal from './RenameCustomizationModal';
import ConfirmationModal from '../../components/confirmation-modal';
import OutputFilenameField from './OutputFilenameField';
import { hasUserName, MVisionAppClient } from '../../store/configurationTarget/mvision-client-list';
import { appConfigSelectors } from '../../store/appConfig/appConfigSlice';
import { configurationTargetSelectors } from '../../store/configurationTarget/configurationTargetSlice';
import { notEmpty } from '../../util/filter';
import { getModelTypeLangKey } from '../../i18n';
import TooltipWrapper from '../../components/tooltip-wrapper';
import { useConcurrentSaveError } from '../../hooks/useConcurrentSaveError';
import { ConcurrentSaveErrorAlert } from '../../components/ConcurrentSaveErrorAlert';

import './customization-page.css';


export type CustomizationNameAndModelId = { customizationName: string; modelId: string; }


interface CustomizationToolbarProps {
    customizationOutputId?: string,
    currentView: CustomizationObjectType,
    modelType: ModelType,

    allCustomizationNamesAndModelIds: CustomizationNameAndModelId[],
    isAnyCustomizationModelModified: boolean,
    customizationOutput: CustomizationOutput | undefined,
    customizationBase: CustomizationBase | undefined,
    model: Model | undefined,

    modelCustomizationSaveError: string | null,
    modelCustomizationDataError: string | null,
    concurrentSaveError?: ConcurrentSaveError | null,
    isSavingInProgress: boolean,
    isCustomizationOutputResetInProgress: boolean,
    isCustomizationResetInProgress: boolean,

    onUndo?: () => void,
    onSave?: (currentTarget: MVisionAppClient, force?: boolean) => void,
    onAddNewCustomization?: (customizationBaseId: string, newCustomizationName: string) => void,
    onAddNewOutput?: (customizationOutputId: string, newFilename: string) => void,
    onDeleteCustomization?: (customizationBaseId: string) => void,
    onDeleteOutput?: (customizationOutputId: string) => void,

    /** Function to run when a customization BASE is being reset back to default factory settings. NOTE: you generally only
     * ever need to support EITHER onResetCustomization OR onResetOutput, not both. onResetCustomization should be used
     * for model types that we do not currently intend to support adding multiple output files.
     */
    onResetCustomization?: (customizationBaseId: string) => void,

    /** Function to run when a customization OUTPUT is being reset back to default factory settings. NOTE: you generally only
     * ever need to support EITHER onResetCustomization OR onResetOutput, not both. onResetOutput should be used for model
     * types that DO support adding multiple output files (e.g. Contour+).
     */
    onResetOutput?: (customizationOutputId: string) => void,

    onRenameCustomization?: (customizationBaseId: string, newCustomizationName: string) => void,

    onAddAdvancedStructure?: (customizationOutputId: string, newRoiId: string) => void,

    /** When true, the save button is disabled due to form validation errors. */
    hasFormValidationErrors?: boolean,
}

const CustomizationToolbar = (props: CustomizationToolbarProps) => {
    const { customizationOutputId, currentView, modelType,
        allCustomizationNamesAndModelIds, isAnyCustomizationModelModified,
        customizationOutput, customizationBase, model,
        modelCustomizationSaveError, modelCustomizationDataError, concurrentSaveError, isSavingInProgress, isCustomizationResetInProgress, isCustomizationOutputResetInProgress,
        onUndo, onSave, onAddNewCustomization, onAddNewOutput, onDeleteCustomization, onDeleteOutput, onResetCustomization, onResetOutput,
        onRenameCustomization,
        onAddAdvancedStructure,
        hasFormValidationErrors = false, } = props;

    const { t } = useTranslation();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isUndoConfirmationModalOpen, setUndoConfirmationModalOpenState] = useState(false);
    const [isSaveConfirmationModalOpen, setSaveConfirmationModalOpenState] = useState(false);
    const [isAddNewCustomizationModalOpen, setAddNewCustomizationModalOpen] = useState(false);
    const [isAddNewFileModalOpen, setAddNewFileModalOpen] = useState(false);
    const [isRenameCustomizationModalOpen, setRenameCustomizationModalOpen] = useState(false);
    const [isDeleteCustomizationModalOpen, setDeleteCustomizationModalOpen] = useState(false);
    const [isDeleteOutputModalOpen, setDeleteOutputModalOpen] = useState(false);
    const [isResetCustomizationModalOpen, setResetCustomizationModalOpen] = useState(false);
    const [isResetOutputModalOpen, setResetOutputModalOpen] = useState(false);
    const concurrentError = useConcurrentSaveError(concurrentSaveError);
    const [duplicatedCustomizationName, setDuplicatedCustomizationName] = useState('');
    const [duplicatedOutputFilename, setDuplicatedOutputFilename] = useState('');
    const [duplicatedCustomizationNameValidation, setDuplicatedCustomizationNameValidation] = useState(defaultDuplicatedModelNameValidation);

    const currentTarget = useSelector(configurationTargetSelectors.selectCurrent);
    const appConfig = useSelector(appConfigSelectors.selectAppConfig);

    const noChanges = !isAnyCustomizationModelModified;

    const handleToggleDropdownOpen = useCallback(() => {
        setIsDropdownOpen(!isDropdownOpen);
    }, [isDropdownOpen]);

    const handleOpenUndoDialog = useCallback(() => {
        setUndoConfirmationModalOpenState(true);
    }, []);

    const handleCloseUndoDialog = useCallback(() => {
        setUndoConfirmationModalOpenState(false);
    }, []);

    const handleOpenSaveDialog = useCallback(() => {
        setSaveConfirmationModalOpenState(true);
        concurrentError.undismiss();
    }, [concurrentError]);

    const handleCloseSaveDialog = useCallback(() => {
        setSaveConfirmationModalOpenState(false);
        concurrentError.dismiss();
    }, [concurrentError]);

    const handleOpenAddNewCustomizationModalDialog = useCallback(() => {
        setAddNewCustomizationModalOpen(true);
    }, []);

    const handleCloseAddNewCustomizationModalDialog = useCallback(() => {
        setAddNewCustomizationModalOpen(false);
    }, []);

    const handleOpenAddNewFileModalDialog = useCallback(() => {
        setAddNewFileModalOpen(true);
    }, []);

    const handleCloseAddNewFileModalDialog = useCallback(() => {
        setAddNewFileModalOpen(false);
    }, []);

    const handleOpenRenameCustomizationModalDialog = useCallback(() => {
        setRenameCustomizationModalOpen(true);
    }, []);

    const handleCloseRenameCustomizationModalDialog = useCallback(() => {
        setRenameCustomizationModalOpen(false);
    }, []);

    const handleOpenDeleteCustomizationModalDialog = useCallback(() => {
        setDeleteCustomizationModalOpen(true);
    }, []);

    const handleCloseDeleteCustomizationModalDialog = useCallback(() => {
        setDeleteCustomizationModalOpen(false);
    }, []);

    const handleOpenDeleteOutputModalDialog = useCallback(() => {
        setDeleteOutputModalOpen(true);
    }, []);

    const handleCloseDeleteOutputModalDialog = useCallback(() => {
        setDeleteOutputModalOpen(false);
    }, []);

    const handleOpenResetCustomizationModalDialog = useCallback(() => {
        setResetCustomizationModalOpen(true);
    }, []);

    const handleCloseResetCustomizationModalDialog = useCallback(() => {
        setResetCustomizationModalOpen(false);
    }, []);

    const handleOpenResetOutputModalDialog = useCallback(() => {
        setResetOutputModalOpen(true);
    }, []);

    const handleCloseResetOutputModalDialog = useCallback(() => {
        setResetOutputModalOpen(false);
    }, []);

    const handleUndo = useCallback(() => {
        if (onUndo && !isSavingInProgress) {
            onUndo();
            handleCloseUndoDialog();
        }
    }, [onUndo, handleCloseUndoDialog, isSavingInProgress]);

    const handleSave = useCallback(() => {
        if (onSave && !isSavingInProgress) {
            if (!currentTarget) {
                throw new Error('No configuration target set -- cannot save');
            }
            onSave(currentTarget, concurrentError.forceOverwrite);
            handleCloseSaveDialog();
        }
    }, [onSave, currentTarget, isSavingInProgress, handleCloseSaveDialog, concurrentError.forceOverwrite]);

    const handleAddNewCustomization = useCallback(() => {
        if (onAddNewCustomization && customizationBase) {
            onAddNewCustomization(customizationBase.id, duplicatedCustomizationName);
        }

        setAddNewCustomizationModalOpen(false);
        setDuplicatedCustomizationName('');
    }, [onAddNewCustomization, customizationBase, duplicatedCustomizationName]);

    const handleAddNewOutput = useCallback(() => {
        if (onAddNewOutput && customizationOutputId) {
            onAddNewOutput(customizationOutputId, duplicatedOutputFilename);
        }

        setAddNewFileModalOpen(false);
        setDuplicatedOutputFilename('');
    }, [onAddNewOutput, customizationOutputId, duplicatedOutputFilename]);

    const handleCustomizationDelete = useCallback(() => {
        handleCloseDeleteCustomizationModalDialog();
        if (onDeleteCustomization && customizationBase) {
            onDeleteCustomization(customizationBase.id);
        }
    }, [onDeleteCustomization, handleCloseDeleteCustomizationModalDialog, customizationBase]);

    const handleOutputDelete = useCallback(() => {
        handleCloseDeleteOutputModalDialog();
        if (onDeleteOutput && customizationOutput) {
            onDeleteOutput(customizationOutput.id);
        }
    }, [onDeleteOutput, handleCloseDeleteOutputModalDialog, customizationOutput]);

    const handleCustomizationReset = useCallback(() => {
        handleCloseResetCustomizationModalDialog();
        if (onResetCustomization && customizationBase) {
            onResetCustomization(customizationBase.id);
        }
    }, [handleCloseResetCustomizationModalDialog, onResetCustomization, customizationBase]);

    const handleOutputReset = useCallback(() => {
        handleCloseResetOutputModalDialog();
        if (onResetOutput && customizationOutput) {
            onResetOutput(customizationOutput.id);
        }
    }, [onResetOutput, handleCloseResetOutputModalDialog, customizationOutput]);

    const handleAddAdvancedStructure = useCallback(() => {
        if (onAddAdvancedStructure && customizationOutputId) {
            const newRoiId = generateNewId();
            onAddAdvancedStructure(customizationOutputId, newRoiId);
        }
    }, [onAddAdvancedStructure, customizationOutputId]);

    const handleDuplicatedCustomizationNameChange = useCallback((newCustomizationName: string) => {
        setDuplicatedCustomizationName(newCustomizationName);
        if (customizationBase && model) {
            const validationResult = validateDuplicatedModelName(allCustomizationNamesAndModelIds, newCustomizationName, customizationBase.modelId);
            setDuplicatedCustomizationNameValidation(validationResult);
        } else {
            throw new Error('Could not retrieve matching customization base and/or model -- cannot validate new name');
        }
    }, [allCustomizationNamesAndModelIds, customizationBase, model]);

    const handleDuplicatedOutputFilenameChange = useCallback((newFilename: string) => {
        setDuplicatedOutputFilename(newFilename);
    }, []);

    const isSaveModalActuallyOpen = isSaveConfirmationModalOpen || isSavingInProgress || concurrentError.isActive;

    const allowRenameCustomization = customizationBase !== undefined && customizationBase.customizationName !== DEFAULT_MODEL_CUSTOMIZATION_NAME;
    const allowRemoveCustomization = customizationBase !== undefined && customizationBase.customizationName !== DEFAULT_MODEL_CUSTOMIZATION_NAME;
    const allowRemoveOutput = customizationBase !== undefined && customizationBase.outputs.length > 1;

    const ellipsisMenuItems = useMemo(() => {
        return [
            onRenameCustomization ? (
                <TooltipWrapper
                    key="rename-customization-wrapper"
                    tooltipContent={!allowRenameCustomization ? t('customizationToolbar.renameCustomization.default.warning') : undefined}
                >
                    <DropdownItem
                        key="rename-customization"
                        isDisabled={!allowRenameCustomization}
                        icon={<PencilAltIcon />}
                        onClick={handleOpenRenameCustomizationModalDialog}>
                        {t('customizationToolbar.renameCustomization', { customizationName: customizationBase ? ` "${customizationBase.customizationName}"` : '' })}
                    </DropdownItem>
                </TooltipWrapper>
            ) : null,
            onDeleteCustomization ? (
                <TooltipWrapper
                    key="remove-customization-wrapper"
                    tooltipContent={!allowRemoveCustomization ? t('customizationToolbar.removeCustomization.default.warning') : undefined}
                >
                    <DropdownItem
                        key="remove-customization"
                        isDisabled={!allowRemoveCustomization || model === undefined || model.customizations.length <= 1}
                        icon={<TrashIcon />}
                        onClick={handleOpenDeleteCustomizationModalDialog}>
                        {t('customizationToolbar.removeCustomization', { customizationName: customizationBase ? `"${customizationBase.customizationName}"` : '' })}
                    </DropdownItem>
                </TooltipWrapper>
            ) : null,
            onDeleteOutput ? (
                <TooltipWrapper
                    key="remove-output-wrapper"
                    tooltipContent={!allowRemoveOutput ? t('customizationToolbar.customizationOutput.warning.cannotRemoveLastOutput') : undefined}
                >
                    <DropdownItem
                        key="remove-output"
                        isDisabled={currentView !== CustomizationObjectType.CustomizationOutput || !allowRemoveOutput}
                        icon={<TrashIcon />}
                        onClick={handleOpenDeleteOutputModalDialog}>
                        {t('customizationToolbar.removeTargetFile', { targetFileName: customizationOutput ? `"${customizationOutput.filename}"` : '' })}
                    </DropdownItem>
                </TooltipWrapper>
            ) : null,
            onResetCustomization ? (
                <DropdownItem
                    key="reset-customization"
                    data-testid="reset-target-customization-menu-item"
                    isDisabled={currentView !== CustomizationObjectType.CustomizationOutput && currentView !== CustomizationObjectType.CustomizationBase}
                    title={t('customizationToolbar.resetTargetCustomization.title')}
                    icon={<HistoryIcon />}
                    onClick={handleOpenResetCustomizationModalDialog}>
                    {t('customizationToolbar.resetTargetCustomization', { targetCustomization: customizationBase ? `"${customizationBase.customizationName}"` : '' })}
                </DropdownItem>
            ) : null,
            onResetOutput ? (
                <DropdownItem
                    key="reset-output"
                    data-testid="reset-target-file-menu-item"
                    isDisabled={currentView !== CustomizationObjectType.CustomizationOutput}
                    title={t('customizationToolbar.resetTargetFile.title')}
                    icon={<HistoryIcon />}
                    onClick={handleOpenResetOutputModalDialog}>
                    {t('customizationToolbar.resetTargetFile', { targetFileName: customizationOutput ? `"${customizationOutput.filename}"` : '' })}
                </DropdownItem>
            ) : null
        ].filter(notEmpty);
    }, [onRenameCustomization, onDeleteCustomization, onDeleteOutput, onResetCustomization, onResetOutput,
        handleOpenDeleteCustomizationModalDialog, handleOpenDeleteOutputModalDialog, handleOpenRenameCustomizationModalDialog,
        handleOpenResetCustomizationModalDialog, handleOpenResetOutputModalDialog,
        allowRenameCustomization, allowRemoveCustomization, allowRemoveOutput, currentView,
        customizationBase, customizationOutput, model, t]);

    return (
        <div className="model-customization-toolbar">
            {appConfig && appConfig.showErrorsInUi && modelCustomizationSaveError !== null && (
                <div className="toolbar-error selectable">
                    <Alert variant="danger" isInline title={t('customizationToolbar.addNewCustomization.Error')}>
                        <p>{modelCustomizationSaveError}</p>
                    </Alert>
                </div>
            )}
            {appConfig && appConfig.showErrorsInUi && modelCustomizationDataError !== null && (
                <div className="toolbar-error selectable">
                    <Alert variant="danger" isInline title="An error occurred">
                        <p>{modelCustomizationDataError}</p>
                    </Alert>
                </div>
            )}
            <Toolbar>
                <ToolbarContent>
                    {onSave && (
                        <ToolbarItem>
                            <Button
                                variant="primary"
                                icon={<SaveIcon />}
                                data-testid="customization-save-all-changes-button"
                                isDisabled={noChanges || hasFormValidationErrors}
                                onClick={handleOpenSaveDialog}>{t('common.saveAllChanges')}</Button>
                        </ToolbarItem>
                    )}
                    {onUndo && (
                        <ToolbarItem>
                            <Button
                                variant="warning"
                                icon={<UndoIcon />}
                                isDisabled={noChanges}
                                onClick={handleOpenUndoDialog}>{t('common.undoAllChanges')}</Button>
                        </ToolbarItem>
                    )}

                    {(currentView === CustomizationObjectType.CustomizationOutput || currentView === CustomizationObjectType.CustomizationBase) &&
                        (onAddNewCustomization || onAddNewOutput) && (
                            <>
                                <ToolbarItem variant="separator" />
                                {onAddNewCustomization && (
                                    <ToolbarItem>
                                        <Button
                                            data-testid="customization-add-new-base-button"
                                            variant="secondary"
                                            icon={<PlusIcon />}
                                            title="Add a new customization for the selected model"
                                            onClick={handleOpenAddNewCustomizationModalDialog}>{t('customizationToolbar.addCustomization')}</Button>
                                    </ToolbarItem>
                                )}
                                {onAddNewOutput && (
                                    <ToolbarItem>
                                        <Button
                                            data-testid="customization-add-new-output-button"
                                            variant="secondary"
                                            icon={<PlusIcon />}
                                            title="Add another target file for the selected customization"
                                            onClick={handleOpenAddNewFileModalDialog}>{t('customizationToolbar.addFile')}</Button>
                                    </ToolbarItem>
                                )}
                            </>)}

                    {currentView === CustomizationObjectType.CustomizationOutput &&
                        (onAddAdvancedStructure) && (
                            <>
                                <ToolbarItem variant="separator" />
                                <ToolbarItem>
                                    <Button
                                        data-testid="add-advanced-roi-button-toolbar"
                                        variant="secondary"
                                        icon={<PlusIcon />}
                                        onClick={handleAddAdvancedStructure}>{t('customizationToolbar.addAdvancedRoi')}</Button>
                                </ToolbarItem>
                            </>)}

                    {(currentView === CustomizationObjectType.CustomizationBase || currentView === CustomizationObjectType.CustomizationOutput)
                        && (onRenameCustomization || onDeleteCustomization || onDeleteOutput || onResetOutput) && (
                            <>
                                <ToolbarItem variant="separator" />
                                <ToolbarItem align={{ default: 'alignRight' }}>
                                    <Dropdown
                                        toggle={(toggleRef) => <MenuToggle ref={toggleRef} onClick={handleToggleDropdownOpen} icon={<EllipsisVIcon />} variant='plain' data-testid="customization-toolbar-actions-toggle"><EllipsisVIcon /></MenuToggle>}
                                        isOpen={isDropdownOpen}
                                        isPlain
                                        isRootMenu
                                        onOpenChange={handleToggleDropdownOpen}
                                        popperProps={{ position: 'right' }}
                                        shouldFocusFirstItemOnOpen={false}>
                                        {ellipsisMenuItems}
                                    </Dropdown>
                                </ToolbarItem>
                            </>)}

                </ToolbarContent>
            </Toolbar>

            <Modal
                variant={ModalVariant.large}
                title={hasUserName(currentTarget) ?
                    (<Trans i18nKey="customizationToolbar.save.confirm.title.hasCurrentUserName">Save {{ modelType: t(getModelTypeLangKey(modelType)) }} customizations for {{ currentUser: currentTarget!.userName }}?</Trans>) :
                    (<Trans i18nKey="customizationToolbar.save.confirm.title.noCurrentUserName">Save {{ modelType: t(getModelTypeLangKey(modelType)) }} customizations?</Trans>)}
                isOpen={isSaveModalActuallyOpen}
                onClose={handleCloseSaveDialog}
                actions={[
                    <Button key="confirmSave" variant="secondary" isLoading={isSavingInProgress} isDisabled={concurrentSaveError != null && !concurrentError.forceOverwrite} onClick={handleSave}>{t('common.saveAll')}</Button>,
                    <Button key="cancel" disabled={isSavingInProgress} variant="tertiary" onClick={handleCloseSaveDialog}>{t('common.cancel')}</Button>
                ]}
                data-testid="save-confirmation-modal"
            >
                {hasUserName(currentTarget) ? (
                    <Trans i18nKey="customizationToolbar.save.confirm.text.hasCurrentUserName">
                        Do you want to overwrite existing {{ modelType: t(getModelTypeLangKey(modelType)) }} customizations for {{ currentUser: currentTarget!.userName }} with your changes? Note
                        that this will save <b>all</b> modifications to customizations for <b>all structures and models</b>. Any previous customizations
                        are lost. You can always revert back to default settings.
                    </Trans>
                ) : (
                    <Trans i18nKey="customizationToolbar.save.confirm.text.noCurrentUserName">
                        Do you want to overwrite existing {{ modelType: t(getModelTypeLangKey(modelType)) }} customizations with your changes? Note
                        that this will save <b>all</b> modifications to customizations for <b>all structures and models</b>. Any previous customizations
                        are lost. You can always revert back to default settings.
                    </Trans>
                )}
                <ConcurrentSaveErrorAlert
                    concurrentSaveError={concurrentSaveError}
                    forceOverwrite={concurrentError.forceOverwrite}
                    onForceOverwriteChange={concurrentError.setForceOverwrite}
                />
            </Modal>

            <Modal
                isOpen={isUndoConfirmationModalOpen}
                onClose={handleCloseUndoDialog}
                variant={ModalVariant.small}
                title={(<Trans i18nKey="customizationToolbar.undo.confirm.title">Confirm undo for {{ modelType: t(getModelTypeLangKey(modelType)) }} changes</Trans>)}
                actions={[
                    <Button key="confirmUndo" variant="danger" onClick={handleUndo}>{t('common.undoAllChanges')}</Button>,
                    <Button key="cancelUndo" variant="secondary" onClick={handleCloseUndoDialog}>{t('common.keepChanges')}</Button>
                ]}
            >
                <Trans i18nKey="customizationToolbar.undo.confirm.text">
                    Are you sure you wish to undo all changes to {{ modelType: t(getModelTypeLangKey(modelType)) }} customizations? Note that this will revert <b>all</b> modifications to customizations
                    for <b>all structures and models</b>. All changes that have not been saved will be lost.
                </Trans>
            </Modal>

            {customizationBase && model && (
                <Modal
                    isOpen={isAddNewCustomizationModalOpen}
                    onClose={handleCloseAddNewCustomizationModalDialog}
                    variant={ModalVariant.medium}
                    title={t('customizationToolbar.addCustomization.title')}
                    className="duplicate-model-modal"
                    position="top"
                    positionOffset="15%"
                    actions={[
                        <Button key="confirmAdd" data-testid="add-new-customization-confirm-button" variant="primary" isDisabled={duplicatedCustomizationNameValidation.validated === 'error' || !duplicatedCustomizationName} onClick={handleAddNewCustomization}>{t('customizationToolbar.addNewCustomization')}</Button>,
                        <Button key="cancelAdd" data-testid="add-new-customization-cancel-button" variant="tertiary" onClick={handleCloseAddNewCustomizationModalDialog}>{t('common.cancel')}</Button>
                    ]}
                    data-testid="add-new-customization-modal"
                >
                    <TextContent className="mv-section">
                        <Text component="p">{t('customizationToolbar.addCustomization.paragraph1')}</Text>
                    </TextContent>

                    <div>
                        <TextContent className="mv-section">
                            <Text component="p">{t('customizationToolbar.addCustomization.paragraph2')}</Text>
                        </TextContent>

                        <TextContent className="mv-section">
                            <Text component="p">{t('customizationToolbar.addCustomization.paragraph3')}</Text>
                        </TextContent>

                        <div className="mv-section">
                            <CustomizationNameField
                                modelName={model.modelName}
                                sourceCustomizationName={customizationBase.customizationName}
                                customizationNameValidation={duplicatedCustomizationNameValidation}
                                onCustomizationNameChange={handleDuplicatedCustomizationNameChange}
                                customizationName={duplicatedCustomizationName}
                            />
                        </div>
                    </div>

                </Modal>
            )}

            {customizationBase && model && customizationOutput && (
                <Modal
                    isOpen={isAddNewFileModalOpen}
                    onClose={handleCloseAddNewFileModalDialog}
                    variant={ModalVariant.medium}
                    title={t('customizationToolbar.addOutput.title')}
                    className="duplicate-model-modal"
                    position="top"
                    positionOffset="15%"
                    actions={[
                        <Button key="confirmAdd" data-testid="add-new-output-confirm-button" variant="primary" isDisabled={!duplicatedOutputFilename} onClick={handleAddNewOutput}>{t('customizationToolbar.addNewTargetFile')}</Button>,
                        <Button key="cancelAdd" data-testid="add-new-output-cancel-button" variant="tertiary" onClick={handleCloseAddNewFileModalDialog}>{t('common.cancel')}</Button>
                    ]}
                    data-testid="add-new-output-modal"
                >
                    <TextContent className="mv-section">
                        <Text component="p">{t('customizationToolbar.addOutput.paragraph1')}</Text>
                    </TextContent>

                    <div>
                        <TextContent className="mv-section">
                            <Text component="p">{t('customizationToolbar.addOutput.paragraph2')}</Text>
                        </TextContent>

                        <TextContent className="mv-section">
                            <Text component="p">{t('customizationToolbar.addOutput.paragraph3')}</Text>
                        </TextContent>

                        <div className="mv-section">
                            <OutputFilenameField
                                modelName={model.modelName}
                                sourceCustomizationName={customizationBase.customizationName}
                                sourceFilename={customizationOutput.filename}
                                duplicatedFilename={duplicatedOutputFilename}
                                onOutputFilenameChange={handleDuplicatedOutputFilenameChange}
                            />
                        </div>
                    </div>

                </Modal>
            )}

            {customizationBase && model && (
                <>
                    {onRenameCustomization && (
                        <RenameCustomizationModal
                            isOpen={isRenameCustomizationModalOpen}
                            onClose={handleCloseRenameCustomizationModalDialog}
                            model={model}
                            customization={customizationBase}
                            onRenameModelCustomization={onRenameCustomization}
                            allCustomizationNamesAndModelIds={allCustomizationNamesAndModelIds}
                        />
                    )}

                    <ConfirmationModal
                        isOpen={isDeleteCustomizationModalOpen}
                        title={t('customizationToolbar.removeCustomization.confirm.title')}
                        onConfirm={handleCustomizationDelete}
                        onClose={handleCloseDeleteCustomizationModalDialog}
                        confirmLabel={t('common.remove')}
                        variant={ModalVariant.small}
                        confirmVariant="danger"
                    >
                        <Trans i18nKey={'customizationToolbar.removeCustomization.confirm.text'}>
                            Are you sure you wish to remove customization {{ customizationName: customizationBase.customizationName }} from {{ modelName: model.modelName }}?
                        </Trans>
                    </ConfirmationModal>
                </>
            )}

            {customizationOutput && customizationBase && model && (
                <ConfirmationModal
                    isOpen={isDeleteOutputModalOpen}
                    title={t('customizationToolbar.removeTargetFile.confirm.title')}
                    onConfirm={handleOutputDelete}
                    onClose={handleCloseDeleteOutputModalDialog}
                    confirmLabel={t('common.remove')}
                    variant={ModalVariant.small}
                    confirmVariant="danger"
                >
                    <Trans i18nKey={'customizationToolbar.removeTargetFile.confirm.text'}>
                        Are you sure you wish to remove target file {{ filename: customizationOutput.filename }} from customization {{ customizationName: customizationBase.customizationName }} (from {{ modelName: model.modelName }})?
                    </Trans>
                </ConfirmationModal>
            )}

            {customizationBase && model && (
                <ConfirmationModal
                    isOpen={isResetCustomizationModalOpen || isCustomizationResetInProgress}
                    title={t('customizationToolbar.resetTargetCustomization.confirm.title')}
                    onConfirm={handleCustomizationReset}
                    onClose={handleCloseResetCustomizationModalDialog}
                    confirmLabel={t('common.reset')}
                    variant={ModalVariant.small}
                    confirmVariant="danger"
                    isLoadingInProgress={isCustomizationResetInProgress}
                    testIdPrefix="reset-target-customization"
                >
                    {t('customizationToolbar.resetTargetCustomization.confirm.message',
                        { targetCustomizationName: customizationBase.customizationName, modelName: model.label })}
                </ConfirmationModal>
            )}

            {customizationOutput && customizationBase && model && (
                <ConfirmationModal
                    isOpen={isResetOutputModalOpen || isCustomizationOutputResetInProgress}
                    title={t('customizationToolbar.resetTargetFile.confirm.title')}
                    onConfirm={handleOutputReset}
                    onClose={handleCloseResetOutputModalDialog}
                    confirmLabel={t('common.reset')}
                    variant={ModalVariant.small}
                    confirmVariant="danger"
                    isLoadingInProgress={isCustomizationOutputResetInProgress}
                    testIdPrefix="reset-target-file"
                >
                    {t('customizationToolbar.resetTargetFile.confirm.message',
                        { targetFileName: customizationOutput.filename, customizationName: customizationBase.customizationName, segmentationModelName: model.label })}
                </ConfirmationModal>
            )}


        </div>
    );
}

export default CustomizationToolbar;
